import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

// ═══════════════════════════════════════════
// SEGMENTI TOČKA
// ═══════════════════════════════════════════
const SEGMENTS = [
  { display: "10% POPUST",    prize: "10% popust na analizu",       prizeType: "10%",     color: "#3b1d8a", bright: "#6d28d9", emoji: "♈" },
  { display: "SREĆA ★",       prize: null,                           prizeType: "LOSE",    color: "#0c0018", bright: "#4c1d95", emoji: "🌙" },
  { display: "20% POPUST",    prize: "20% popust na analizu",       prizeType: "20%",     color: "#1e3a8a", bright: "#2563eb", emoji: "♓" },
  { display: "BESPL.PITANJE", prize: "Besplatno astro pitanje",     prizeType: "PITANJE", color: "#064e3b", bright: "#059669", emoji: "✨" },
  { display: "30% POPUST",    prize: "30% popust na analizu",       prizeType: "30%",     color: "#7f1d1d", bright: "#dc2626", emoji: "♒" },
  { display: "SREĆA ★",       prize: null,                           prizeType: "LOSE",    color: "#0c0018", bright: "#4c1d95", emoji: "🌙" },
  { display: "50% POPUST",    prize: "50% popust na analizu",       prizeType: "50%",     color: "#14532d", bright: "#16a34a", emoji: "♑" },
  { display: "BESPL.ANALIZA", prize: "Besplatna analiza",           prizeType: "ANALIZA", color: "#78350f", bright: "#d97706", emoji: "🔮" },
];

const NUM_SEG = SEGMENTS.length;
const SEG_ANGLE = (2 * Math.PI) / NUM_SEG;

// ═══════════════════════════════════════════
// DNEVNI LIMIT
// ═══════════════════════════════════════════
const SPIN_KEY = "wheel_last_spin_date";
function hasSpunToday(): boolean {
  return localStorage.getItem(SPIN_KEY) === new Date().toISOString().split("T")[0];
}
function markSpunToday() {
  localStorage.setItem(SPIN_KEY, new Date().toISOString().split("T")[0]);
}

// ═══════════════════════════════════════════
// WEB AUDIO ENGINE
// ═══════════════════════════════════════════
let audioCtx: AudioContext | null = null;

function getAudioCtx(): AudioContext {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
  }
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
}

function playTick(speed: number) {
  const ctx = getAudioCtx();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.frequency.value = 600 + speed * 200;
  osc.type = "sine";
  gain.gain.setValueAtTime(0.15, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.04);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.04);
}

function playDrumroll(duration: number): () => void {
  const ctx = getAudioCtx();
  const nodes: OscillatorNode[] = [];
  
  [55, 110].forEach((freq) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = "sawtooth";
    osc.frequency.value = freq;
    gain.gain.setValueAtTime(0.04, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.08, ctx.currentTime + duration * 0.3);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + duration);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + duration + 0.1);
    nodes.push(osc);
  });
  
  return () => nodes.forEach(n => { try { n.stop(); } catch { /* ignore */ } });
}

function playWinFanfare() {
  const ctx = getAudioCtx();
  const melody = [523.25, 659.25, 783.99, 1046.5, 1046.5];
  const times = [0, 0.12, 0.24, 0.36, 0.55];
  melody.forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    osc.type = i === melody.length - 1 ? "triangle" : "sine";
    const t = ctx.currentTime + times[i];
    gain.gain.setValueAtTime(0, t);
    gain.gain.linearRampToValueAtTime(0.45, t + 0.04);
    gain.gain.exponentialRampToValueAtTime(0.001, t + (i === melody.length - 1 ? 0.9 : 0.35));
    osc.start(t);
    osc.stop(t + 1);
  });
  [523.25, 659.25, 783.99].forEach((freq) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    osc.type = "triangle";
    const t = ctx.currentTime + 0.72;
    gain.gain.setValueAtTime(0.18, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 2.0);
    osc.start(t);
    osc.stop(t + 2.1);
  });
}

function playLoseTune() {
  const ctx = getAudioCtx();
  [392, 349.23, 329.63, 261.63].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.frequency.value = freq;
    osc.type = "sine";
    const t = ctx.currentTime + i * 0.22;
    gain.gain.setValueAtTime(0.25, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.35);
    osc.start(t);
    osc.stop(t + 0.4);
  });
}

// ═══════════════════════════════════════════
// CONFETTI
// ═══════════════════════════════════════════
function launchConfetti(canvas: HTMLCanvasElement) {
  const ctx = canvas.getContext("2d")!;
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  const colors = ["#fbbf24", "#7c3aed", "#ec4899", "#3b82f6", "#22c55e", "#f59e0b", "#fff"];
  // Smanjeno sa 180 na 60 za mobilnu optimizaciju
  const particleCount = window.innerWidth < 768 ? 40 : 60;
  const particles = Array.from({ length: particleCount }, () => ({
    x: Math.random() * canvas.width,
    y: -30 - Math.random() * 200,
    vx: (Math.random() - 0.5) * 6,
    vy: Math.random() * 4 + 2,
    color: colors[Math.floor(Math.random() * colors.length)],
    size: Math.random() * 9 + 4,
    rotation: Math.random() * 360,
    rotSpeed: (Math.random() - 0.5) * 12,
    rect: Math.random() > 0.4,
  }));
  let frame = 0;
  // Smanjeno sa 280 na 180 frejmova za kraću animaciju
  const maxFrames = window.innerWidth < 768 ? 120 : 180;
  const go = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    particles.forEach(p => {
      p.y += p.vy; p.x += p.vx; p.rotation += p.rotSpeed; p.vy += 0.06;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate((p.rotation * Math.PI) / 180);
      ctx.fillStyle = p.color;
      if (p.rect) ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
      else { ctx.beginPath(); ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2); ctx.fill(); }
      ctx.restore();
    });
    if (++frame < maxFrames) requestAnimationFrame(go);
    else ctx.clearRect(0, 0, canvas.width, canvas.height);
  };
  go();
}

// ═══════════════════════════════════════════
// ZVJEZDANO NEBO
// ═══════════════════════════════════════════
function initStars(canvas: HTMLCanvasElement): () => void {
  const ctx = canvas.getContext("2d")!;
  canvas.width = window.innerWidth;
  canvas.height = window.innerHeight;
  // Smanjeno sa 220 na 80/120 za mobilnu optimizaciju
  const starCount = window.innerWidth < 768 ? 80 : 120;
  const stars = Array.from({ length: starCount }, () => ({
    x: Math.random() * canvas.width,
    y: Math.random() * canvas.height,
    r: Math.random() * 1.6 + 0.2,
    phase: Math.random() * Math.PI * 2,
    speed: Math.random() * 0.4 + 0.15,
  }));
  let rafId: number;
  const go = () => {
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const t = Date.now() / 2000;
    stars.forEach(s => {
      const a = 0.25 + 0.75 * (0.5 + 0.5 * Math.sin(t * s.speed + s.phase));
      ctx.beginPath();
      ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(255,255,255,${a.toFixed(2)})`;
      ctx.fill();
    });
    rafId = requestAnimationFrame(go);
  };
  go();
  return () => cancelAnimationFrame(rafId);
}

// ═══════════════════════════════════════════
// TOČAK RENDERER
// ═══════════════════════════════════════════
function drawWheel(canvas: HTMLCanvasElement, angle: number) {
  const ctx = canvas.getContext("2d")!;
  const W = canvas.width;
  const cx = W / 2, cy = W / 2;
  const R = cx - 8;
  ctx.clearRect(0, 0, W, W);
  
  const halo = ctx.createRadialGradient(cx, cy, R - 10, cx, cy, R + 20);
  halo.addColorStop(0, "rgba(124,58,237,0.25)");
  halo.addColorStop(1, "rgba(124,58,237,0)");
  ctx.beginPath(); ctx.arc(cx, cy, R + 18, 0, Math.PI * 2);
  ctx.fillStyle = halo; ctx.fill();
  
  for (let i = 0; i < NUM_SEG; i++) {
    const startA = angle + i * SEG_ANGLE - Math.PI / 2;
    const endA = startA + SEG_ANGLE;
    const seg = SEGMENTS[i];
    
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.arc(cx, cy, R, startA, endA);
    ctx.closePath();
    
    const midA = startA + SEG_ANGLE / 2;
    const gx = cx + R * 0.55 * Math.cos(midA);
    const gy = cy + R * 0.55 * Math.sin(midA);
    const grad = ctx.createRadialGradient(gx, gy, 0, cx, cy, R);
    grad.addColorStop(0, seg.bright + "99");
    grad.addColorStop(1, seg.color);
    ctx.fillStyle = grad;
    ctx.fill();
    
    ctx.strokeStyle = "rgba(251,191,36,0.5)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
  
  ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.strokeStyle = "#fbbf24"; ctx.lineWidth = 5; ctx.stroke();
  
  ctx.beginPath(); ctx.arc(cx, cy, R * 0.85, 0, Math.PI * 2);
  ctx.strokeStyle = "rgba(251,191,36,0.12)"; ctx.lineWidth = 1; ctx.stroke();
  
  for (let i = 0; i < NUM_SEG; i++) {
    const midA = angle + i * SEG_ANGLE + SEG_ANGLE / 2 - Math.PI / 2;
    const seg = SEGMENTS[i];
    const tr = R * 0.63;
    
    ctx.save();
    ctx.translate(cx + tr * Math.cos(midA), cy + tr * Math.sin(midA));
    ctx.rotate(midA + Math.PI / 2);
    
    ctx.font = "14px serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ctx.fillText(seg.emoji, 0, -15);
    
    const lines = seg.display.split(".");
    ctx.fillStyle = seg.prizeType === "LOSE" ? "rgba(255,255,255,0.3)" : "#fde68a";
    ctx.font = `bold ${lines[0].length > 7 ? "8.5" : "10"}px Inter,sans-serif`;
    ctx.shadowColor = "rgba(0,0,0,0.8)";
    ctx.shadowBlur = 3;
    if (lines.length === 1) {
      ctx.fillText(lines[0], 0, 2);
    } else {
      ctx.fillText(lines[0], 0, -3);
      ctx.fillText(lines[1], 0, 10);
    }
    ctx.shadowBlur = 0;
    ctx.restore();
  }
  
  const cGrad = ctx.createRadialGradient(cx - 5, cy - 5, 2, cx, cy, 26);
  cGrad.addColorStop(0, "#fde68a");
  cGrad.addColorStop(1, "#b45309");
  ctx.beginPath(); ctx.arc(cx, cy, 26, 0, Math.PI * 2);
  ctx.fillStyle = cGrad; ctx.fill();
  ctx.strokeStyle = "#fbbf24"; ctx.lineWidth = 2.5; ctx.stroke();
  
  ctx.fillStyle = "#4c1d95";
  ctx.font = "bold 20px sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("✦", cx, cy);
}

// ═══════════════════════════════════════════
// GLAVNA KOMPONENTA
// ═══════════════════════════════════════════
type Phase = "email_required" | "ready" | "spinning" | "win" | "lose" | "already_spun";

export default function WheelPage() {
  const navigate = useNavigate();
  const wheelRef = useRef<HTMLCanvasElement>(null);
  const starsRef = useRef<HTMLCanvasElement>(null);
  const confettiRef = useRef<HTMLCanvasElement>(null);
  const angleRef = useRef(0);
  const rafRef = useRef<number>(0);
  const stopDrumRef = useRef<(() => void) | null>(null);
  
  const [phase, setPhase] = useState<Phase>("email_required");
  const [prize, setPrize] = useState<{ display: string; code: string; prizeType: string } | null>(null);
  const [remaining, setRemaining] = useState<number | null>(null);
  const [glowing, setGlowing] = useState(false);
  const [size, setSize] = useState(300);
  const [email, setEmail] = useState("");
  const [emailSaved, setEmailSaved] = useState(false);
  const [emailError, setEmailError] = useState("");
  
  useEffect(() => {
    setSize(Math.min(window.innerWidth - 32, 320));
  }, []);
  useEffect(() => {
    const canvas = starsRef.current;
    if (!canvas) return;
    return initStars(canvas);
  }, []);
  
  useEffect(() => {
    if (wheelRef.current && size > 0) {
      drawWheel(wheelRef.current, 0);
    }
  }, [size]);
  
  useEffect(() => {
    if (hasSpunToday()) {
      setPhase("already_spun");
    } else {
      // Check if email is already saved in localStorage
      const savedEmail = localStorage.getItem("wheel_user_email");
      if (savedEmail) {
        setEmail(savedEmail);
        setEmailSaved(true);
        setPhase("ready");
      } else {
        setPhase("email_required");
      }
    }
  }, []);
  
  useEffect(() => {
    const interval = setInterval(() => setGlowing(g => !g), 1000);
    return () => clearInterval(interval);
  }, []);
  
  useEffect(() => {
    supabase.from("wheel_spins_2026_03_20")
      .select("prize_type")
      .eq("spin_date", new Date().toISOString().split("T")[0])
      .neq("prize_type", "LOSE")
      .then(({ data }) => {
        if (data) setRemaining(Math.max(0, (new Date().getDay() === 0 ? 11 : 10) - data.length));
      });
  }, []);
  
  const handleEmailSubmit = () => {
    if (!email || !email.includes('@')) {
      setEmailError('Molimo unesite validan email');
      return;
    }
    
    // Save email to localStorage
    localStorage.setItem("wheel_user_email", email);
    setEmailSaved(true);
    setEmailError("");
    setPhase("ready");
  };
  
  const handleSpin = useCallback(async () => {
    if (hasSpunToday()) { setPhase("already_spun"); return; }
    getAudioCtx();
    setPhase("spinning");
    markSpunToday();
    
    const sessionId = (() => {
      let s = localStorage.getItem("wheel_session_id");
      if (!s) { s = `s_${Date.now()}_${Math.random().toString(36).substr(2, 8)}`; localStorage.setItem("wheel_session_id", s); }
      return s;
    })();
    
    // Use saved email instead of anonymous email
    const userEmail = localStorage.getItem("wheel_user_email") || `anon_${sessionId}@wheel.local`;
    
    const { data } = await supabase.functions.invoke("spin_wheel_improved_2026_03_28", {
      body: { email: userEmail, sessionId }
    });
    
    const prizeType: string = data?.success ? (data.prize || "LOSE") : "LOSE";
    
    const DURATION = 5500;
    stopDrumRef.current = playDrumroll(DURATION / 1000);
    
    // Pronađi target segment
    let targetIdx = SEGMENTS.findIndex(s => s.prizeType === prizeType);
    
    // Ako nije pronađen ili je LOSE, nasumično izaberi LOSE segment
    if (targetIdx < 0 || prizeType === "LOSE") {
      const loseSegments = SEGMENTS.map((s, i) => s.prizeType === "LOSE" ? i : -1).filter(i => i >= 0);
      targetIdx = loseSegments[Math.floor(Math.random() * loseSegments.length)];
    }
    
    const startAngle = angleRef.current;
    
    // LOGIKA:
    // - Strelica je na vrhu točka (pozicija -PI/2)
    // - Segment i se crta od (angle + i*SEG_ANGLE - PI/2) do (angle + i*SEG_ANGLE + SEG_ANGLE - PI/2)
    // - Centar segmenta i je na: angle + i*SEG_ANGLE + SEG_ANGLE/2 - PI/2
    // - Da bi segment targetIdx bio pod strelicom, njegov centar mora biti na -PI/2
    // - Dakle: finalAngle + targetIdx*SEG_ANGLE + SEG_ANGLE/2 - PI/2 = -PI/2
    // - finalAngle = -targetIdx*SEG_ANGLE - SEG_ANGLE/2
    // - Ali rotiramo 9 puta za efekat: finalAngle = 9*2*PI - targetIdx*SEG_ANGLE - SEG_ANGLE/2
    
    const spins = 9; // Broj rotacija
    const targetAngle = -targetIdx * SEG_ANGLE - SEG_ANGLE / 2;
    const finalAngle = spins * Math.PI * 2 + targetAngle + (Math.random() - 0.5) * (SEG_ANGLE * 0.3);
    const totalRotation = finalAngle - (startAngle % (Math.PI * 2));
    const startTime = performance.now();
    let lastSeg = -1;
    let lastTickTime = 0;
    
    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / DURATION, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      const speed = 1 - Math.pow(1 - progress, 3);
      
      const angle = startAngle + totalRotation * eased;
      angleRef.current = angle;
      if (wheelRef.current) drawWheel(wheelRef.current, angle);
      
      const normAngle = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
      const curSeg = Math.floor(((Math.PI * 2 - normAngle) % (Math.PI * 2)) / SEG_ANGLE);
      const minInterval = 50 + (1 - speed) * 300;
      if (curSeg !== lastSeg && (now - lastTickTime) > minInterval) {
        playTick(speed);
        lastSeg = curSeg;
        lastTickTime = now;
      }
      
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        if (stopDrumRef.current) stopDrumRef.current();
        
        if (prizeType === "LOSE" || !data?.success) {
          playLoseTune();
          setPrize(null);
          setPhase("lose");
        } else {
          playWinFanfare();
          const seg = SEGMENTS.find(s => s.prizeType === prizeType);
          setPrize({
            display: seg?.prize || prizeType,
            code: data.discountCode || "",
            prizeType,
          });
          setPhase("win");
          if (confettiRef.current) launchConfetti(confettiRef.current);
        }
      }
    };
    
    rafRef.current = requestAnimationFrame(animate);
  }, []);
  
  const saveEmail = async () => {
    if (!email || !email.includes('@')) {
      alert('Molimo unesite validan email');
      return;
    }
    
    const sessionId = localStorage.getItem("wheel_session_id") || "";
    const today = new Date().toISOString().split('T')[0];
    
    console.log('Saving email:', email, 'for session:', sessionId, 'date:', today);
    
    try {
      // Ažuriraj wheel_spins_2026_03_20 sa pravim email-om
      const { data, error } = await supabase
        .from('wheel_spins_2026_03_20')
        .update({ email: email })
        .eq('session_id', sessionId)
        .eq('spin_date', today)
        .select();
      
      console.log('Update result:', { data, error });
      
      if (error) {
        console.error('Error saving email:', error);
        alert(`Greška pri čuvanju email-a: ${error.message}`);
        return;
      }
      
      if (!data || data.length === 0) {
        console.error('No rows updated');
        alert('Nije pronađen zapis za ažuriranje. Pokušajte ponovo.');
        return;
      }
      
      console.log('Email saved successfully:', data);
      setEmailSaved(true);
      alert('✅ Email uspešno sačuvan!');
    } catch (error) {
      console.error('Error:', error);
      alert('Greška pri čuvanju email-a. Pokušajte ponovo.');
    }
  };
  
  
  const goToOrder = () => {
    const params = new URLSearchParams();
    if (prize?.code) params.set("wheel_code", prize.code);
    if (prize?.prizeType) params.set("discount", prize.prizeType);
    navigate(`/?${params.toString()}`);
    setTimeout(() => {
      document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
    }, 400);
  };
  
  return (
    <div
      className="relative min-h-screen overflow-hidden flex flex-col items-center justify-center"
      style={{ background: "radial-gradient(ellipse at 50% 30%, #1a003e 0%, #0a0015 60%, #000008 100%)" }}
    >
      <canvas ref={starsRef} className="absolute inset-0 pointer-events-none" style={{ zIndex: 0 }} />
      <canvas ref={confettiRef} className="fixed inset-0 pointer-events-none" style={{ zIndex: 100 }} />
      
      <div className="relative z-10 flex flex-col items-center w-full px-4 py-8" style={{ maxWidth: 440 }}>
        
        <div className="text-center mb-5">
          <div className="text-5xl mb-2" style={{ filter: "drop-shadow(0 0 20px rgba(251,191,36,0.7))" }}>🔮</div>
          <h1 className="font-bold mb-1" style={{
            fontSize: "clamp(1.4rem, 5vw, 2rem)",
            color: "#fbbf24",
            textShadow: "0 0 40px rgba(251,191,36,0.6), 0 0 80px rgba(124,58,237,0.4)"
          }}>
            Astrološki Točak Sreće
          </h1>
          <p style={{ color: "rgba(196,181,253,0.8)", fontSize: "0.85rem" }}>
            Zvezde odlučuju — ti samo vrtiš ✨
          </p>
          {remaining !== null && remaining > 0 && (
            <p style={{ color: "#fbbf24", fontSize: "0.75rem", marginTop: "0.3rem", opacity: 0.8 }}>
              🎁 Preostalo nagrada danas: <strong>{remaining}</strong>
            </p>
          )}
        </div>
        
        {/* Email Input Screen */}
        {phase === "email_required" && (
          <div className="w-full max-w-md mx-auto mb-6">
            <div style={{
              background: "rgba(124,58,237,0.1)",
              border: "2px solid rgba(251,191,36,0.3)",
              borderRadius: "1rem",
              padding: "2rem 1.5rem",
              textAlign: "center",
            }}>
              <div style={{ fontSize: "2.5rem", marginBottom: "1rem" }}>📧</div>
              <h3 style={{
                color: "#fbbf24",
                fontSize: "1.3rem",
                fontWeight: "bold",
                marginBottom: "0.5rem",
              }}>
                Unesite Email
              </h3>
              <p style={{
                color: "rgba(196,181,253,0.8)",
                fontSize: "0.9rem",
                marginBottom: "1.5rem",
                lineHeight: 1.5,
              }}>
                Da bismo vam poslali nagradu i ekskluzivne ponude, molimo vas unesite email adresu.
              </p>
              
              <input
                type="email"
                placeholder="vas@email.com"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setEmailError("");
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleEmailSubmit()}
                style={{
                  width: "100%",
                  padding: "0.9rem 1rem",
                  borderRadius: "0.5rem",
                  border: emailError ? "2px solid #ef4444" : "2px solid rgba(251,191,36,0.3)",
                  background: "rgba(0,0,0,0.3)",
                  color: "#fff",
                  fontSize: "1rem",
                  marginBottom: "1rem",
                  outline: "none",
                }}
              />
              
              {emailError && (
                <p style={{
                  color: "#ef4444",
                  fontSize: "0.85rem",
                  marginBottom: "1rem",
                }}>
                  {emailError}
                </p>
              )}
              
              <button
                onClick={handleEmailSubmit}
                style={{
                  width: "100%",
                  padding: "1rem",
                  borderRadius: "9999px",
                  background: "linear-gradient(90deg, #b45309, #d97706, #fbbf24)",
                  color: "#1e003e",
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  border: "none",
                  cursor: "pointer",
                  boxShadow: "0 0 20px rgba(251,191,36,0.4)",
                  transition: "all 0.3s ease",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.05)";
                  e.currentTarget.style.boxShadow = "0 0 40px rgba(251,191,36,0.6)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "0 0 20px rgba(251,191,36,0.4)";
                }}
              >
                Nastavi ka Točku 🎰
              </button>
              
              <p style={{
                color: "rgba(196,181,253,0.5)",
                fontSize: "0.75rem",
                marginTop: "1rem",
              }}>
                🔒 Vaš email je siguran i neće biti deljen
              </p>
            </div>
          </div>
        )}
        
        {/* Wheel - Show only when email is provided */}
        {phase !== "email_required" && (
          <>
        <div className="relative mb-6" style={{
          filter: `drop-shadow(0 0 ${phase === "spinning" ? "60px" : "30px"} rgba(124,58,237,0.5))`,
          transition: "filter 0.5s"
        }}>
          <div style={{
            position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)",
            zIndex: 20, filter: "drop-shadow(0 0 8px rgba(251,191,36,1))"
          }}>
            <div style={{
              width: 0, height: 0,
              borderLeft: "15px solid transparent",
              borderRight: "15px solid transparent",
              borderTop: "32px solid #fbbf24"
            }} />
          </div>
          <canvas
            ref={wheelRef}
            width={size}
            height={size}
            style={{ display: "block", borderRadius: "50%" }}
          />
        </div>
        
        {phase === "ready" && (
          <div className="text-center space-y-3 w-full">
            <button
              onClick={handleSpin}
              style={{
                width: "100%", maxWidth: 280, padding: "1.1rem 2rem",
                borderRadius: "9999px",
                background: "linear-gradient(90deg, #b45309, #d97706, #fbbf24, #d97706)",
                color: "#1e003e", fontWeight: "bold", fontSize: "1.15rem",
                border: "none", cursor: "pointer",
                boxShadow: glowing
                  ? "0 0 50px rgba(251,191,36,0.9), 0 0 100px rgba(251,191,36,0.4)"
                  : "0 0 20px rgba(251,191,36,0.4)",
                transition: "box-shadow 0.8s ease",
                display: "block", margin: "0 auto",
              }}
            >
              🎰 Zavrti Točak!
            </button>
            <p style={{ color: "rgba(196,181,253,0.5)", fontSize: "0.72rem" }}>
              Jedna šansa dnevno · Bez registracije
            </p>
          </div>
        )}
        
        {phase === "spinning" && (
          <div className="text-center">
            <p style={{ color: "#fbbf24", fontSize: "1.1rem", fontWeight: "bold" }} className="animate-pulse">
              ✨ Zvezde se usklađuju...
            </p>
            <p style={{ color: "rgba(196,181,253,0.6)", fontSize: "0.8rem", marginTop: "0.5rem" }}>
              Sudbina je u pokretu 🌀
            </p>
          </div>
        )}
        
        {phase === "win" && prize && (
          <div className="text-center w-full space-y-4" style={{ animation: "popIn 0.5s cubic-bezier(0.175,0.885,0.32,1.275)" }}>
            <div style={{ fontSize: "3.5rem", filter: "drop-shadow(0 0 20px gold)" }}>🎉</div>
            <h2 style={{ color: "#fbbf24", fontSize: "1.5rem", fontWeight: "bold", textShadow: "0 0 20px rgba(251,191,36,0.6)" }}>
              Čestitamo!
            </h2>
            <p style={{ color: "white", fontSize: "1.05rem" }}>
              Vaša nagrada: <strong style={{ color: "#fbbf24" }}>{prize.display}</strong>
            </p>
            {prize.code && (
              <div style={{
                background: "rgba(251,191,36,0.12)", border: "2px dashed #fbbf24",
                borderRadius: "1rem", padding: "1rem 1.5rem",
              }}>
                <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.72rem", marginBottom: "0.4rem", textTransform: "uppercase", letterSpacing: "0.1em" }}>
                  Vaš kod za popust
                </p>
                <p style={{ color: "#fbbf24", fontSize: "1.6rem", fontWeight: "bold", letterSpacing: "0.12em", fontFamily: "monospace" }}>
                  {prize.code}
                </p>
              </div>
            )}
            
            {/* Email already saved - show confirmation */}
            <p style={{ color: "#22c55e", fontSize: "0.9rem", marginTop: "1rem", marginBottom: "1rem" }}>
              ✅ Poslat ćemo vam podsetnik na: <strong>{email}</strong>
            </p>
            
            <button
              onClick={goToOrder}
              style={{
                width: "100%", padding: "1rem", borderRadius: "9999px",
                background: "linear-gradient(90deg, #d97706, #fbbf24)",
                color: "#1e003e", fontWeight: "bold", fontSize: "1.1rem",
                border: "none", cursor: "pointer",
                boxShadow: "0 0 30px rgba(251,191,36,0.5)",
              }}
            >
              🛒 Naruči odmah sa popustom →
            </button>
            <p style={{ color: "rgba(196,181,253,0.5)", fontSize: "0.72rem" }}>
              Kod je aktiviran automatski na formi
            </p>
          </div>
        )}
        
        {phase === "lose" && (
          <div className="text-center w-full space-y-4">
            <div style={{ fontSize: "3rem" }}>🌙</div>
            <h2 style={{ color: "rgba(255,255,255,0.85)", fontSize: "1.35rem", fontWeight: "bold" }}>
              Ovaj put nije tvoja sreća...
            </h2>
            <p style={{ color: "rgba(196,181,253,0.65)", fontSize: "0.9rem", lineHeight: 1.6 }}>
              Zvezde te čuvaju za sutra.<br />
              Svaki dan nova šansa — vrati se u ponoć. 🌟
            </p>
            <div className="space-y-3 w-full">
              <button
                onClick={goToOrder}
                style={{
                  width: "100%", padding: "1rem", borderRadius: "9999px",
                  background: "linear-gradient(90deg, #6d28d9, #7c3aed, #9333ea)",
                  color: "white", fontWeight: "bold", fontSize: "1rem",
                  border: "none", cursor: "pointer",
                  boxShadow: "0 0 20px rgba(124,58,237,0.4)",
                }}
              >
                🔮 Naruči analizu bez popusta →
              </button>
              <p style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.72rem" }}>
                ili se vrati sutra za novu šansu
              </p>
            </div>
          </div>
        )}
        
        {phase === "already_spun" && (
          <div className="text-center w-full space-y-4">
            <div style={{ fontSize: "3rem" }}>⏳</div>
            <h2 style={{ color: "#fbbf24", fontSize: "1.3rem", fontWeight: "bold" }}>
              Već si vrtao danas!
            </h2>
            <p style={{ color: "rgba(196,181,253,0.7)", fontSize: "0.9rem" }}>
              Jedna šansa dnevno. Vrati se u ponoć — zvezde te čekaju. 🌙
            </p>
            <button
              onClick={() => navigate("/")}
              style={{
                width: "100%", padding: "1rem", borderRadius: "9999px",
                background: "linear-gradient(90deg, #6d28d9, #9333ea)",
                color: "white", fontWeight: "bold", fontSize: "1rem",
                border: "none", cursor: "pointer",
              }}
            >
              Idi na analizu →
            </button>
          </div>
        )}
        </>
        )}
      </div>
      
      <style>{`
        @keyframes popIn {
          0% { opacity: 0; transform: scale(0.7); }
          100% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}